import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Bookmark, BookmarkPlus, RefreshCw, Search, SlidersHorizontal, X } from 'lucide-react';

import type {
  ComicCard as ComicCardData,
  ComicSource,
  Page,
  SearchQuery,
  SourceId,
} from '@/sources/types';
import { getAllSources, getRegistryDiagnostics } from '@/sources/registry';
import { JMCOMIC_SOURCE_ID, parseJmAlbumLookup } from '@/sources/jmcomic/source';
import { ComicCard } from '@/components/ComicCard';
import { PaginatedGrid } from '@/components/PaginatedGrid';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useSourceAuth, useSourceAuthLoader } from '@/features/auth';
import { BlockedComicCard, useBlacklist } from '@/features/blacklist';
import { SourceErrorBanner } from '@/features/reader';
import {
  FilterPanel,
  SaveSearchDialog,
  SavedSearchesMenu,
  countActiveFilters,
  aggregateDisplaySources,
  aggregateSourceSlot,
  isEmptyQuery,
  listRecentSearches,
  MAX_RECENT_SEARCHES,
  recordRecentSearch,
  runAggregateSourceSearch,
  searchQueryToUrlParams,
  upsertRecentSearch,
  useSearchUrlState,
  type RecentSearch,
} from '@/features/search';
import {
  createSavedSearch,
  listSavedSearchesForSource,
  type SavedSearch,
} from '@/db/savedSearches';
import { useAccountAuthReady } from '@/features/account';
import { requestAccountDataAutoSync } from '@/features/account/accountDataAutoSync';
import { cn } from '@/lib/utils';
import { useRestoreComicCardOnReturn, useScrollRestoration } from '@/lib/useScrollRestoration';
import {
  readAggregateSearchCache,
  writeAggregateSearchCache,
  type AggregateSourceSnapshot,
} from '@/features/search/aggregateSearchCache';
import { RecommendedPage } from './RecommendedPage';

type SourceScope = SourceId | 'all';

interface SourceResult {
  source: ComicSource;
  items: ComicCardData[];
  error?: unknown;
  loading: boolean;
}

/**
 * Primary discovery surface: recommendations when the query is empty, then
 * source-scoped or aggregate results after the user starts a search.
 */
export function Home() {
  const sources = useMemo(() => getAllSources(), []);
  const diag = useMemo(() => getRegistryDiagnostics(), []);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { isBlocked } = useBlacklist();
  const initialScope = useMemo(() => scopeFromParams(params, sources), [params, sources]);
  const [scope, setScope] = useState<SourceScope>(initialScope);
  const activeSource = scope !== 'all' ? sources.find((source) => source.id === scope) : undefined;
  const schema = activeSource?.searchFilters;
  const { query } = useSearchUrlState(schema);
  const { auth, ready: authReady } = useSourceAuth(activeSource);
  const loadSourceAuthForSource = useSourceAuthLoader();
  const accountAuthReady = useAccountAuthReady();
  const [draft, setDraft] = useState(query.q ?? '');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [savedOpen, setSavedOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [aggregateResults, setAggregateResults] = useState<SourceResult[]>([]);
  const [aggregateLoading, setAggregateLoading] = useState(false);
  const [browseRefreshVersion, setBrowseRefreshVersion] = useState(0);
  const savedScope = scope === 'all' ? '*' : scope;
  useScrollRestoration('discover');
  useRestoreComicCardOnReturn();

  useEffect(() => {
    setDraft(query.q ?? '');
  }, [query.q]);

  useEffect(() => {
    setScope(initialScope);
  }, [initialScope]);

  useEffect(() => {
    if (scope === 'all') return;
    if (activeSource) return;
    setScope('all');
  }, [activeSource, scope]);

  useEffect(() => {
    if (!suggestionsOpen) return;
    let cancelled = false;
    void Promise.all([listRecentSearches(), listSavedSearchesForSource(savedScope)]).then(
      ([recent, saved]) => {
        if (cancelled) return;
        setRecentSearches((current) => mergeRecentSearches(recent, current));
        setSavedSearches(saved);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [savedScope, suggestionsOpen]);

  const hasAnyQuery = !isEmptyQuery(query);
  const recommendationMode = !hasAnyQuery;
  const displayedDiscoverySources = useMemo(() => {
    const displayed = aggregateDisplaySources(sources);
    if (activeSource?.id !== 'ehentai') return displayed;
    return displayed.map((source) =>
      aggregateSourceSlot(source) === aggregateSourceSlot(activeSource) ? activeSource : source,
    );
  }, [activeSource, sources]);
  const scopeSources = recommendationMode ? displayedDiscoverySources : sources;
  const activeFiltersCount = countActiveFilters(query);
  const queryKey = useMemo(() => searchQueryToUrlParams(query).toString(), [query]);
  const browseCacheVersion = `refresh:${browseRefreshVersion}`;
  const canShowSuggestions = suggestionsOpen && draft.trim().length === 0;
  const canOpenSearchFilters = scope === 'all' || Boolean(schema);
  const filterButtonLabel = scope === 'all' ? 'Source filters' : 'Filters';

  const scopedFetcher = useCallback(
    async (page: number, cursor?: string): Promise<Page<ComicCardData>> => {
      if (!activeSource) throw new Error('Pick a source before browsing.');
      if (isEmptyQuery(query)) throw new Error('Search requires a query or filter.');
      return activeSource.search({ ...query, page, cursor }, auth);
    },
    [activeSource, auth, query],
  );

  const shouldAggregate = scope === 'all' && hasAnyQuery;
  const aggregateCacheKey = `home:${params.toString()}:${browseCacheVersion}`;

  useEffect(() => {
    if (!shouldAggregate) {
      setAggregateResults([]);
      setAggregateLoading(false);
      return;
    }
    if (!accountAuthReady) {
      setAggregateResults([]);
      setAggregateLoading(true);
      return;
    }
    const cached = readAggregateSearchCache(aggregateCacheKey);
    if (cached && cached.every((result) => !result.loading)) {
      setAggregateResults(restoreAggregateSnapshots(sources, cached));
      setAggregateLoading(false);
      return;
    }
    const controller = new AbortController();
    const searchable = sources.filter((source) => source.capabilities.search);
    setAggregateLoading(true);
    setAggregateResults(
      aggregateDisplaySources(searchable).map((source) => ({ source, items: [], loading: true })),
    );
    void runAggregateSourceSearch({
      sources: searchable,
      query,
      loadAuth: loadSourceAuthForSource,
      signal: controller.signal,
      fetchPage: (source, aggregateQuery, sourceAuth) => source.search(aggregateQuery, sourceAuth),
      onResult: (next) => {
        setAggregateResults((current) =>
          current.map((result) =>
            aggregateSourceSlot(result.source) === aggregateSourceSlot(next.source) ? next : result,
          ),
        );
      },
    }).finally(() => {
      if (!controller.signal.aborted) setAggregateLoading(false);
    });
    return () => {
      controller.abort();
    };
  }, [
    accountAuthReady,
    aggregateCacheKey,
    loadSourceAuthForSource,
    query,
    shouldAggregate,
    sources,
  ]);

  useEffect(() => {
    if (!shouldAggregate) return;
    if (aggregateLoading) return;
    if (aggregateResults.length === 0 || aggregateResults.some((result) => result.loading)) return;
    writeAggregateSearchCache(aggregateCacheKey, snapshotAggregateResults(aggregateResults));
  }, [aggregateCacheKey, aggregateLoading, aggregateResults, shouldAggregate]);

  function updateKeyword(nextKeyword: string, opts: { replace?: boolean } = {}) {
    const next: SearchQuery = { ...query };
    if (nextKeyword) next.q = nextKeyword;
    else delete next.q;
    setScopedQuery(next, opts);
  }

  function clearFilters() {
    setScopedQuery(withDraftKeyword({}));
  }

  function withDraftKeyword(nextQuery: SearchQuery): SearchQuery {
    const keyword = draft.trim();
    if (keyword) return { ...nextQuery, q: keyword };
    const next = { ...nextQuery };
    delete next.q;
    return next;
  }

  function setScopedQuery(nextQuery: SearchQuery, opts: { replace?: boolean } = {}) {
    const next = withRecommendationParams(searchQueryToUrlParams(nextQuery), params);
    if (scope !== 'all') next.set('scope', scope);
    navigate({ pathname: '/', search: next.toString() }, { replace: opts.replace ?? false });
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const q = draft.trim();
    const albumId = scope === JMCOMIC_SOURCE_ID ? parseJmAlbumLookup(q) : undefined;
    if (activeSource && albumId) {
      setSuggestionsOpen(false);
      navigate(`/source/${activeSource.id}/comic/${encodeURIComponent(albumId)}`, {
        state: {
          back: {
            fromPath: `/${params.toString() ? `?${params.toString()}` : ''}`,
            fromLabel: activeSource.name,
          },
        },
      });
      return;
    }
    updateKeyword(q);
    setSuggestionsOpen(false);
    if (q) {
      const params = searchQueryToUrlParams({ ...query, q });
      const url =
        scope === 'all' ? `/?${params.toString()}` : `/?scope=${scope}&${params.toString()}`;
      setRecentSearches((rows) => upsertRecentSearch(rows, { q, scope, url }));
      void recordRecentSearch({ q, scope, url });
    }
  }

  function navigateSearchShortcut(row: { q?: string; url: string }) {
    setSuggestionsOpen(false);
    const url = new URL(row.url, window.location.origin);
    const shortcutScope = url.searchParams.get('scope') ?? 'all';
    const albumId =
      shortcutScope === JMCOMIC_SOURCE_ID
        ? parseJmAlbumLookup(row.q ?? url.searchParams.get('q') ?? '')
        : undefined;
    if (albumId) {
      navigate(`/source/${JMCOMIC_SOURCE_ID}/comic/${encodeURIComponent(albumId)}`, {
        state: {
          back: {
            fromPath: `${url.pathname}${url.search}`,
            fromLabel: activeSource?.id === JMCOMIC_SOURCE_ID ? activeSource.name : '禁漫天堂',
          },
        },
      });
      return;
    }
    navigate(row.url);
  }

  function selectScope(
    nextScope: SourceScope,
    nextQuery: SearchQuery = query,
    opts: { keepFiltersOpen?: boolean } = {},
  ) {
    setScope(nextScope);
    setFiltersOpen(opts.keepFiltersOpen ?? false);
    const next = withRecommendationParams(searchQueryToUrlParams(nextQuery), params);
    if (nextScope === 'all') next.delete('scope');
    else next.set('scope', nextScope);
    navigate({ pathname: '/', search: next.toString() }, { replace: true });
  }

  function renderItem(c: ComicCardData) {
    if (isBlocked(c)) return <BlockedComicCard comic={c} />;
    const fromLabel =
      scope === 'all' ? (query.q ? 'Discover' : 'Sources') : (activeSource?.name ?? 'Discover');
    return (
      <ComicCard
        comic={c}
        backState={{
          fromPath: `/${params.toString() ? `?${params.toString()}` : ''}`,
          fromLabel,
        }}
      />
    );
  }

  function openSourceScope(nextScope: SourceId) {
    selectScope(nextScope);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function refreshBrowse() {
    setBrowseRefreshVersion((version) => version + 1);
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="sticky top-[var(--app-safe-area-top)] z-30 -mx-4 border-b border-border/60 bg-background/95 px-4 pb-3 pt-4 backdrop-blur md:static md:mx-0 md:rounded-xl md:border md:p-4">
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Discover</h1>
          </div>
          <div className="flex items-center gap-2">
            {hasAnyQuery && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft('');
                  setScopedQuery({});
                }}
              >
                Clear
              </Button>
            )}
            {!recommendationMode && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={refreshBrowse}
                disabled={scope === 'all' && aggregateLoading}
                className="gap-1.5"
                aria-label="Refresh sources"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', aggregateLoading && 'animate-spin')} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            )}
          </div>
        </div>
        <form onSubmit={handleSubmit} className="relative flex items-center gap-2" role="search">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              role="searchbox"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onFocus={() => setSuggestionsOpen(true)}
              onBlur={() => window.setTimeout(() => setSuggestionsOpen(false), 120)}
              placeholder={
                scope === 'all' ? 'Search all sources' : `Search ${activeSource?.name ?? 'source'}`
              }
              className="h-11 w-full rounded-xl border border-input bg-background pl-9 pr-9 text-sm outline-none ring-offset-background focus:ring-2 focus:ring-ring"
              aria-label="Search"
            />
            {draft && (
              <button
                type="button"
                onClick={() => {
                  setDraft('');
                  updateKeyword('');
                }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-accent"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {canShowSuggestions && (
              <SearchSuggestions
                recent={recentSearches}
                saved={savedSearches}
                onPickRecent={navigateSearchShortcut}
                onPickSaved={navigateSearchShortcut}
              />
            )}
          </div>
          <Button type="submit" size="icon" className="h-11 w-11 shrink-0" aria-label="Search">
            <Search className="h-4 w-4" />
          </Button>
          {canOpenSearchFilters && (
            <Button
              type="button"
              size="icon"
              variant="outline"
              onClick={() => setFiltersOpen((open) => !open)}
              className="relative h-11 w-11 shrink-0"
              aria-label={filterButtonLabel}
            >
              <SlidersHorizontal className="h-4 w-4" />
              {scope !== 'all' && activeFiltersCount > 0 && (
                <span
                  className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground"
                  aria-label={`${activeFiltersCount} active filters`}
                >
                  {activeFiltersCount}
                </span>
              )}
            </Button>
          )}
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => setSavedOpen(true)}
            className="h-11 w-11 shrink-0"
            aria-label="Saved"
          >
            <Bookmark className="h-4 w-4" />
          </Button>
          {hasAnyQuery && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => setSaveOpen(true)}
              className="h-11 w-11 shrink-0"
              aria-label="Save"
            >
              <BookmarkPlus className="h-4 w-4" />
            </Button>
          )}
        </form>
        <div
          className="mt-3 flex max-w-full items-center gap-2 overflow-x-auto overscroll-x-contain pb-1 [scrollbar-width:thin]"
          role="group"
          aria-label="Search scope"
        >
          <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {recommendationMode ? 'Discover from' : 'Search in'}
          </span>
          <ScopeChip active={scope === 'all'} onClick={() => selectScope('all')}>
            All
          </ScopeChip>
          {scopeSources.map((source) => (
            <ScopeChip
              key={source.id}
              active={scope === source.id}
              onClick={() => selectScope(source.id)}
            >
              {source.name}
            </ScopeChip>
          ))}
        </div>
      </section>

      {filtersOpen && scope === 'all' && (
        <AllRefinePanel
          sources={sources}
          onSelectSource={(sourceId) => {
            selectScope(sourceId, withDraftKeyword(query), { keepFiltersOpen: true });
          }}
          onClose={() => setFiltersOpen(false)}
        />
      )}

      {filtersOpen && scope !== 'all' && schema && (
        <FilterPanel
          schema={schema}
          value={query}
          onApply={(next) => {
            setScopedQuery(withDraftKeyword(next));
            setFiltersOpen(false);
          }}
          onClear={() => {
            clearFilters();
            setFiltersOpen(false);
          }}
          onClose={() => setFiltersOpen(false)}
          className="md:rounded-xl"
        />
      )}

      {recommendationMode ? (
        <RecommendedPage />
      ) : scope === 'all' ? (
        <AggregateResults
          results={aggregateResults}
          loading={aggregateLoading}
          renderItem={renderItem}
          onOpenSource={openSourceScope}
        />
      ) : activeSource ? (
        authReady ? (
          <PaginatedGrid<ComicCardData>
            key={`${activeSource.id}:${auth ? 'auth' : 'anon'}:${queryKey}:${browseCacheVersion}`}
            fetcher={scopedFetcher}
            resetKey={`${activeSource.id}:${auth ? 'auth' : 'anon'}:${queryKey}:${browseCacheVersion}`}
            cacheKey={`home:${params.toString()}:${auth ? 'auth' : 'anon'}:${browseCacheVersion}`}
            ariaLabel={`${activeSource.name} source feed`}
            itemKey={(c) => `${c.source}-${c.id}`}
            renderItem={(c) => renderItem(c)}
            emptyState={hasAnyQuery ? <EmptySearchState /> : undefined}
            errorBanner={(err, retry) => (
              <SourceErrorBanner error={err} sourceId={activeSource.id} onRetry={retry} />
            )}
            autoLoadOnScroll
          />
        ) : (
          <div
            aria-label={`${activeSource.name} source feed loading`}
            className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="aspect-[3/4] w-full" />
            ))}
          </div>
        )
      ) : sources.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No sources registered yet</CardTitle>
            <CardDescription>
              Source modules live under <code>src/sources/&lt;id&gt;/</code> and are
              auto-discovered. The <code>_template/</code> module shows the expected shape.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {diag.errors.length > 0 && (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-destructive">Registry load errors</CardTitle>
            <CardDescription>
              Some files under <code>src/sources/</code> did not load as valid sources.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-xs">
              {diag.errors.map((e) => (
                <li key={e.path}>
                  <code>{e.path}</code>: {e.reason}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <SavedSearchesMenu scope={savedScope} open={savedOpen} onOpenChange={setSavedOpen} />
      <SaveSearchDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        suggestedName={query.q?.trim() || 'Saved search'}
        onSave={async (name) => {
          const params = searchQueryToUrlParams(query).toString();
          const url = scope === 'all' ? `/?${params}` : `/?scope=${scope}&${params}`;
          await createSavedSearch({ name, source: savedScope, url });
          requestAccountDataAutoSync();
        }}
      />
    </div>
  );
}

function withRecommendationParams(
  next: URLSearchParams,
  current: URLSearchParams,
): URLSearchParams {
  for (const [key, value] of current) {
    if (key.startsWith('option.') || key.startsWith('extra.')) next.set(key, value);
  }
  return next;
}

function scopeFromParams(params: URLSearchParams, sources: ComicSource[]): SourceScope {
  const rawScope = params.get('scope');
  if (!rawScope || rawScope === 'all') return 'all';
  if (sources.some((source) => source.id === rawScope)) return rawScope as SourceId;
  return 'all';
}

function snapshotAggregateResults(results: SourceResult[]): AggregateSourceSnapshot[] {
  return results.map((result) => ({
    sourceId: result.source.id,
    items: result.items,
    error: result.error,
    loading: result.loading,
  }));
}

function restoreAggregateSnapshots(
  sources: ComicSource[],
  snapshots: AggregateSourceSnapshot[],
): SourceResult[] {
  const byId = new Map(sources.map((source) => [source.id, source]));
  return snapshots.flatMap((snapshot) => {
    const source = byId.get(snapshot.sourceId);
    if (!source) return [];
    return [{ source, items: snapshot.items, error: snapshot.error, loading: snapshot.loading }];
  });
}

function mergeRecentSearches(
  persisted: RecentSearch[],
  optimistic: RecentSearch[],
): RecentSearch[] {
  const byId = new Map<string, RecentSearch>();
  for (const row of [...persisted, ...optimistic]) {
    const existing = byId.get(row.id);
    if (!existing || row.createdAt > existing.createdAt) {
      byId.set(row.id, row);
    }
  }
  return [...byId.values()].sort((a, b) => b.createdAt - a.createdAt).slice(0, MAX_RECENT_SEARCHES);
}

function ScopeChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'h-8 shrink-0 rounded-full border px-3 text-xs font-medium',
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border/60 text-muted-foreground hover:bg-accent/30 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function SearchSuggestions({
  recent,
  saved,
  onPickRecent,
  onPickSaved,
}: {
  recent: RecentSearch[];
  saved: SavedSearch[];
  onPickRecent: (row: RecentSearch) => void;
  onPickSaved: (row: SavedSearch) => void;
}) {
  return (
    <div className="absolute inset-x-0 top-[calc(100%+0.5rem)] z-40 rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-xl">
      {recent.length === 0 && saved.length === 0 ? (
        <p className="text-sm text-muted-foreground">Recent and saved searches will appear here.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {recent.length > 0 && (
            <section>
              <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Recent searches
              </h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {recent.slice(0, 6).map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onPickRecent(row)}
                    className="rounded-full border border-border/60 bg-background px-3 py-1.5 text-xs"
                  >
                    {row.q}
                  </button>
                ))}
              </div>
            </section>
          )}
          {saved.length > 0 && (
            <section>
              <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Saved searches
              </h2>
              <div className="mt-2 flex flex-col gap-1">
                {saved.slice(0, 4).map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onPickSaved(row)}
                    className="flex items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent"
                  >
                    <span className="truncate">{row.name}</span>
                    <Badge variant="outline" className="ml-2 text-[10px]">
                      {row.source === 'all' || row.source === '*' ? 'all' : row.source}
                    </Badge>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function AllRefinePanel({
  sources,
  onSelectSource,
  onClose,
}: {
  sources: ComicSource[];
  onSelectSource: (sourceId: SourceId) => void;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-col gap-4 rounded-md border border-border/60 bg-card/30 p-4 md:rounded-xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold tracking-tight">Choose a source for filters</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Advanced filters vary by source. Pick one source to keep the keyword and add its filter
            set.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-muted-foreground hover:bg-accent/40 hover:text-foreground"
          aria-label="Close refine panel"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        {sources.map((source) => (
          <button
            key={source.id}
            type="button"
            onClick={() => onSelectSource(source.id)}
            className="rounded-lg border border-border/60 bg-background p-3 text-left hover:bg-accent/30"
          >
            <span className="block text-sm font-medium">{source.name}</span>
            <span className="mt-1 block text-xs text-muted-foreground">
              {source.searchFilters ? 'Full filters available' : 'Basic browsing'}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function AggregateResults({
  results,
  loading,
  renderItem,
  onOpenSource,
}: {
  results: SourceResult[];
  loading: boolean;
  renderItem: (comic: ComicCardData) => React.ReactNode;
  onOpenSource: (sourceId: SourceId) => void;
}) {
  const previewCount = 6;

  if (loading && results.length === 0) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="aspect-[3/4] w-full" />
        ))}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-5">
      {results.map((result) => (
        <section key={result.source.id} className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2 border-b border-border/40 pb-1">
            <h2 className="text-sm font-semibold">{result.source.name}</h2>
            <button
              type="button"
              onClick={() => onOpenSource(result.source.id)}
              className="text-xs text-muted-foreground underline"
            >
              See more
            </button>
          </div>
          {result.error !== undefined ? (
            <SourceErrorBanner error={result.error} sourceId={result.source.id} />
          ) : result.loading ? (
            <div
              aria-label={`${result.source.name} horizontal results loading`}
              className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 [scrollbar-width:thin]"
            >
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="aspect-[3/4] w-36 shrink-0 snap-start sm:w-40" />
              ))}
            </div>
          ) : result.items.length === 0 ? (
            <p className="text-xs text-muted-foreground">No results from {result.source.name}.</p>
          ) : (
            <div
              aria-label={`${result.source.name} horizontal results`}
              className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 [scrollbar-width:thin]"
            >
              {result.items.slice(0, previewCount).map((comic) => (
                <div
                  key={`${comic.source}-${comic.id}`}
                  className="w-36 shrink-0 snap-start sm:w-40"
                >
                  {renderItem(comic)}
                </div>
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

function EmptySearchState() {
  return (
    <div className="rounded-md border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
      No results for that search.
    </div>
  );
}
